// B6 — the loan's legal-compliance home (tab key "legal-review", display
// label "Compliance"). The Track 1 Legal Ops surface (module check cards,
// Material Event Register, upcoming/overdue cadence, open exceptions)
// relocated here from the legal-ops tab key (now Quality Assurance) — a UI
// relocation only: entities, permissions (Compliance.*), endpoints and the
// LegalOpsCadenceWorker are untouched. Structure: due-date strip for the
// legal cadence → legal-compliance checks + material events → the annual
// attorney review history (Track 3 LLCR), whose privilege-aware rendering
// is preserved exactly — A3 / E4 / ADMIN get full detail + workbench
// links, B3 / B4 / B5 get the server-shaped summary only.

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Button, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  useLoanInsurance,
  useLoanUccFilings,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { useLegalOpsHistory } from "@/hooks/compliance/useCompliance";
import { useLoanLegalReviews } from "@/hooks/compliance/useLegalReview";
import { isFullReview } from "@/api/compliance/legalReviewTypes";
import type {
  LoanLegalReviewDto,
  LoanLegalReviewStatus,
  LoanLegalReviewSummaryDto,
} from "@/api/compliance/legalReviewTypes";
import type { LegalOpsModule } from "@/api/compliance/types";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import type { LoanDetailTabId } from "@/pages/portfolioAdmin/LoanDetailPage";
import { useDateFormatter } from "@/utils/datetime";
import { ModuleStatusCards } from "../legalOps/ModuleStatusCards";
import { RecordCheckDrawer } from "../legalOps/RecordCheckDrawer";
import { MaterialEventsSection } from "../legalOps/MaterialEventsSection";
import { UpcomingOverdueSection } from "../legalOps/UpcomingOverdueSection";
import { OpenExceptionsPanel } from "../legalOps/OpenExceptionsPanel";
import { LegalCadenceStrip } from "../legalOps/LegalCadenceStrip";
import { CovenantBreachStrip } from "../complianceIntake/CovenantBreachStrip";
import { ReportedIssuesSection } from "../complianceIntake/ReportedIssuesSection";

interface LegalReviewTabProps {
  loan: LoanSummaryDto;
}

interface DrawerState {
  module: LegalOpsModule;
  calendarItemId: string | null;
  periodLabel: string | null;
}

const STATUS_BADGE: Record<
  LoanLegalReviewStatus,
  React.ComponentProps<typeof Badge>["status"]
> = {
  Scoping: "draft",
  Fieldwork: "info",
  Draft: "draft",
  InReview: "review",
  Finalized: "finalized",
  Reissued: "archived",
};

export function LegalReviewTab({ loan }: LegalReviewTabProps) {
  const auth = useAuth();
  const isPrivileged =
    auth.hasRoleCode("A3") ||
    auth.hasRoleCode("E4") ||
    auth.hasRoleCode("ADMIN");
  const canRecordCheck =
    auth.hasPermission("Compliance.RecordLegalOpsCheck") ||
    auth.hasRoleCode("ADMIN");
  const canCapture =
    auth.hasPermission("Compliance.ManageMaterialEvents") ||
    auth.hasRoleCode("ADMIN");
  const canManage = canCapture;
  const canResolve =
    auth.hasPermission("Compliance.ResolveExceptions") ||
    auth.hasRoleCode("ADMIN");

  const historyQuery = useLegalOpsHistory(loan.id);
  const uccQuery = useLoanUccFilings(loan.id);
  const insuranceQuery = useLoanInsurance(loan.id);
  const reviewsQuery = useLoanLegalReviews(loan.id);

  const [, setParams] = useSearchParams();
  const [drawer, setDrawer] = useState<DrawerState | null>(null);

  const openLinkedTab = (tabId: LoanDetailTabId) => {
    const next = new URLSearchParams(window.location.search);
    next.set("tab", tabId);
    setParams(next, { replace: false });
  };

  return (
    <div className="flex flex-col gap-space-4" data-testid="legal-review-tab">
      <Card>
        <div className="flex items-center justify-between gap-space-3">
          <div>
            <h2 className="font-display font-bold text-h3 m-0">Compliance</h2>
            <p className="text-caption text-gray-mid mt-space-1 m-0">
              Legal-compliance checks, material events and the Track 3 Annual
              Loan-Level Legal Compliance Review (LLCR) history.
              {!isPrivileged && (
                <>
                  {" "}
                  Working notes, scoping memos, and memos are restricted to
                  attorney (A3) and compliance oversight (E4) roles.
                </>
              )}
            </p>
          </div>
          {isPrivileged && (
            <Link
              to={`/compliance?section=legal-reviews&loanId=${loan.id}`}
              data-testid="legal-review-tab-open-workspace"
            >
              <Button variant="secondary" size="sm">
                Open workspace
              </Button>
            </Link>
          )}
        </div>
      </Card>

      {/* (top) Open covenant breaches — every LoanFailure-backed condition
          + DSCR breach on this loan, severity-tinted, linking to the owning
          tab. Hidden when none (calm by default). */}
      <CovenantBreachStrip loanId={loan.id} />

      {/* Due-date strip for the legal cadence items. */}
      <LegalCadenceStrip loanId={loan.id} />

      {/* B4's triage queue — reported issues land here and on the
          Compliance cockpit; ?report={id} deep-links the detail drawer. */}
      <ReportedIssuesSection loanId={loan.id} />

      {/* (middle) Legal-compliance checks + material events — relocated
          from the legal-ops tab body (UI move only). */}
      {historyQuery.isError ? (
        <Card>
          <p className="text-small text-status-error m-0" role="alert">
            Could not load Legal Ops history. Refresh to retry.
          </p>
        </Card>
      ) : (
        <>
          <ModuleStatusCards
            loanId={loan.id}
            history={historyQuery.data}
            uccFilings={uccQuery.data ?? []}
            insurancePolicies={insuranceQuery.data ?? []}
            canRecord={canRecordCheck}
            onRecordCheck={(module) =>
              setDrawer({ module, calendarItemId: null, periodLabel: null })
            }
            onOpenLinkedTab={openLinkedTab}
          />

          <UpcomingOverdueSection
            loanId={loan.id}
            onPrefillRecordCheck={(args) =>
              setDrawer({
                module: args.module,
                calendarItemId: args.calendarItemId,
                periodLabel: args.periodLabel,
              })
            }
          />

          <MaterialEventsSection
            loanId={loan.id}
            canCapture={canCapture}
            canManage={canManage}
          />

          <OpenExceptionsPanel loanId={loan.id} canResolve={canResolve} />
        </>
      )}

      {/* (bottom) Annual attorney review history — a distinct,
          clearly-labeled section; privilege-aware rendering preserved. */}
      <Card>
        <h3 className="font-display font-bold text-h3 m-0">
          Annual legal review (LLCR)
        </h3>
        <p className="text-caption text-gray-mid mt-space-1 m-0">
          Attorney-led annual review engagements, by audit year.
        </p>
      </Card>

      {reviewsQuery.isLoading ? (
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            Loading legal review history…
          </p>
        </Card>
      ) : reviewsQuery.isError ? (
        <Card>
          <p className="text-small text-status-error m-0" role="alert">
            Could not load legal review history. Refresh to retry.
          </p>
        </Card>
      ) : (reviewsQuery.data ?? []).length === 0 ? (
        <Card data-testid="legal-review-empty">
          <p className="text-small text-gray-mid italic m-0">
            No legal review engagements have been opened for this loan yet.
          </p>
        </Card>
      ) : (
        <ul
          aria-label="Legal review history"
          className="divide-y divide-border-light bg-white-pure border border-border-light rounded-lg"
        >
          {(reviewsQuery.data ?? [])
            .slice()
            .sort((a, b) => b.auditYear - a.auditYear)
            .map((r) => (
              <ReviewRow
                key={r.id}
                review={r}
                loanId={loan.id}
                isPrivileged={isPrivileged}
              />
            ))}
        </ul>
      )}

      {drawer && (
        <RecordCheckDrawer
          loanId={loan.id}
          module={drawer.module}
          prefillCalendarItemId={drawer.calendarItemId}
          prefillPeriodLabel={drawer.periodLabel}
          open={!!drawer}
          onClose={() => setDrawer(null)}
        />
      )}
    </div>
  );
}

function ReviewRow({
  review,
  loanId,
  isPrivileged,
}: {
  review: LoanLegalReviewDto | LoanLegalReviewSummaryDto;
  loanId: string;
  isPrivileged: boolean;
}) {
  const findingsCount = isFullReview(review)
    ? review.modules.filter((m) => m.conclusion === "ExceptionRaised").length
    : review.exceptionRaisedCount;
  const { date } = useDateFormatter();

  return (
    <li
      className="px-space-3 py-space-3 flex items-center gap-space-3"
      data-testid={`legal-review-row-${review.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-space-2">
          <span className="font-display font-semibold text-small">
            Audit year {review.auditYear}
          </span>
          <Badge status={STATUS_BADGE[review.status]}>{review.status}</Badge>
          {review.signedAt && (
            <span className="text-caption text-gray-mid">
              Signed {date(review.signedAt)}
            </span>
          )}
        </div>
        <div className="mt-space-1 text-caption text-gray-mid">
          {findingsCount > 0 ? (
            <span data-testid={`legal-review-findings-${review.id}`}>
              {findingsCount} finding{findingsCount === 1 ? "" : "s"}
            </span>
          ) : (
            <span data-testid={`legal-review-findings-${review.id}`}>
              No findings
            </span>
          )}
          {review.finalLlcrReportSubmissionId && (
            <>
              {" "}
              ·{" "}
              <Link
                to={`/reports/${review.finalLlcrReportSubmissionId}`}
                className="text-gold-shadow hover:underline"
                data-testid={`legal-review-llcr-${review.id}`}
              >
                Signed LLCR
              </Link>
            </>
          )}
          {!isPrivileged && (
            <span
              className="ml-space-2 italic"
              data-testid={`legal-review-summary-only-${review.id}`}
            >
              · summary view (privileged content restricted)
            </span>
          )}
        </div>
      </div>
      {isPrivileged && (
        <Link
          to={`/compliance/legal-review/${review.id}?loanId=${loanId}`}
          data-testid={`legal-review-open-${review.id}`}
        >
          <Button variant="tertiary" size="sm">
            Open
          </Button>
        </Link>
      )}
    </li>
  );
}
